import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'
import { useQuery } from 'react-query'

// Libs
import { fetchProductData, MenuType, ProductInfoType, SkuItemInfoType } from '@/api/fetchProductData'

// Constants
import { QUERY_KEY } from '@/constants/queryKey'
import { isLiveMallClub } from '@/constants/systemType'

// Types

export const useProductData = (productId: any, productDataSSR?: any) => {
  const [productInfo, setProductInfo] = useState<Partial<ProductInfoType>>({})
  const [menu, setMenu] = useState<MenuType[]>([])
  const [skuItemInfo, setSkuItemInfo] = useState<SkuItemInfoType[]>([])
  const [filteredItems, setFilteredItems] = useState<any>({})
  const [quantity, setQuantity] = useState(0)
  const [searchKey, setSearchKey] = useState<string[]>([])

  const router = useRouter()

  const { data: productData, isFetched: productDataIsFetched } = useQuery(
    [QUERY_KEY.FETCH_PRODUCT_DATA, { productId }],
    () => fetchProductData({ productId: Number(productId) }),
    {
      enabled: !!productId && router.isReady,
      keepPreviousData: false,
      cacheTime: 0,
      initialData: productDataSSR,
    },
  )

  useEffect(() => {
    if (productDataIsFetched) {
      const newSkuItemInfo = productData?.entity?.skuItemInfo?.filter((data: any) => data.status === 100)

      setMenu(productData?.entity?.specMenu)
      setProductInfo(productData?.entity?.productInfo)
      setSkuItemInfo(newSkuItemInfo)
    }

    return () => {
      // productDataIsFetched 改變前先清空
      setFilteredItems([])
      setSkuItemInfo([])
    }
  }, [productData, productDataIsFetched])

  useEffect(() => {
    const getFirstAvailableSku = (): string[] => {
      const firstAvailableSku = skuItemInfo.find((sku) => sku.stockQuantity > 0)
      return firstAvailableSku?.profile?.specs.map((spec) => String(spec.attrId)) || []
    }

    if (menu?.length > 0) {
      // searchKey 有幾個 item 代表有幾種規格
      // 在選之前都會是空字串
      // 選了之後相對應的 item 就會是該規格的值

      // Live Mall Club 預設選擇第一個有庫存的規格
      const newSearchKey = isLiveMallClub ? getFirstAvailableSku() : menu.map(() => '')
      setSearchKey(newSearchKey)
    }
  }, [menu])

  useEffect(() => {
    const filteredItems = skuItemInfo
      .filter((data) => {
        const dataSpec = data.profile?.specs.map((data) => String(data.attrId))
        return data.stockQuantity > 0 && searchKey?.every((v) => v === '' || dataSpec?.includes(v))
      })
      .filter((data) => data.status === 100)

    const searchKeyList = searchKey?.filter((v) => v !== '')

    // Todo: 要把預設 quantity 改成總量
    if (searchKeyList.length === searchKey.length) {
      setQuantity(filteredItems.length > 0 ? filteredItems[0].stockQuantity : 0)
    } else {
      setQuantity(0)
    }

    setFilteredItems(filteredItems)
  }, [searchKey])

  useEffect(() => {
    if (filteredItems.length === 1) {
      const singleSkuSpecs = filteredItems[0]?.profile?.specs?.map((spec: any) => String(spec.attrId)) || []
      const autoSelectionItems = singleSkuSpecs.filter((item: any) => item !== '')

      if (autoSelectionItems.length > 0) {
        setSearchKey(autoSelectionItems)
      }
    }
  }, [JSON.stringify(filteredItems)])

  return {
    productInfo,
    isShowProduct: productData?.entity?.isShow,
    menu,
    skuItemInfo: productDataIsFetched ? skuItemInfo : [],
    searchKey,
    quantity,
    filteredItems,
    handleFilteredItemsChange: setFilteredItems,
    handleQuantityChange: setQuantity,
    handleSearchKeyChange: setSearchKey,
    productDataIsFetched,
    productEntity: productData?.entity,
  }
}
